import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { sportsMenuFullQuery } from '@/api/queries';
import {
  getCategoryFlag,
  getTournamentOrCategoryBadge,
  isFlagBadgeSrc,
  sportIcon,
  toSlug,
} from '@/shell/sports-menu-utils';
import { HorizontalScrollRow, Icon, SportIcon } from '@/ui';

export interface SportsContextSwitcherProps {
  /** Active sport slug from the route (e.g. `football`). */
  sportSlug: string;
  /** Optional selected category slug; tournaments row shows when set. */
  selectedCategorySlug?: string;
  /** Optional selected tournament slug from the route. */
  selectedTournamentSlug?: string;
  onCategorySelect: (categorySlug: string | null) => void;
  onSportSelect: (sportSlug: string) => void;
  className?: string;
}

/**
 * Mobile SportsContextSwitcher (Figma): three horizontal sliders for
 * Sports → Categories → Tournaments. Desktop keeps SideNav instead.
 */
export function SportsContextSwitcher({
  sportSlug,
  selectedCategorySlug,
  selectedTournamentSlug,
  onCategorySelect,
  onSportSelect,
  className,
}: SportsContextSwitcherProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage, isLoadingTranslations } = useTranslations();
  const { data: sports, isPending, isError } = useQuery(sportsMenuFullQuery());

  const sportsTranslationsReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'sports');
  const shouldHoldForTranslations = !sportsTranslationsReady && isLoadingTranslations;

  const translateSportsLabel = (id: number, fallbackLabel: string) => {
    const byNameKey = fallbackLabel.trim();
    const byIdKey = String(id);
    if (byNameKey && i18n.exists(byNameKey, { ns: 'sports' })) {
      return t(byNameKey, { ns: 'sports', defaultValue: fallbackLabel });
    }
    if (i18n.exists(byIdKey, { ns: 'sports' })) {
      return t(byIdKey, { ns: 'sports', defaultValue: fallbackLabel });
    }
    return fallbackLabel;
  };

  const sortedSports = sports ? [...sports].sort((a, b) => a.Position - b.Position) : [];
  const activeSport =
    sortedSports.find((sport) => toSlug(sport.Name) === sportSlug) ?? sortedSports[0];
  const categories = activeSport
    ? [...activeSport.Categories].sort((a, b) => a.Position - b.Position)
    : [];
  const selectedCategory = selectedCategorySlug
    ? categories.find((category) => toSlug(category.Name) === selectedCategorySlug)
    : undefined;
  const tournaments = selectedCategory
    ? [...selectedCategory.Tournaments].sort((a, b) => a.Position - b.Position)
    : [];
  const activeSportSlug = activeSport ? toSlug(activeSport.Name) : sportSlug;

  if (isPending || shouldHoldForTranslations) {
    return (
      <div className={cn('flex flex-col gap-3', className)} aria-busy="true">
        <HorizontalScrollRow aria-label={t('Sports', { defaultValue: 'Sports' })} className="items-center">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 min-w-[72px] shrink-0 animate-pulse rounded-md bg-bg-elevated" />
          ))}
        </HorizontalScrollRow>
        <HorizontalScrollRow aria-label={t('Categories', { defaultValue: 'Categories' })}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-10 w-28 shrink-0 animate-pulse rounded-md bg-bg-elevated" />
          ))}
        </HorizontalScrollRow>
      </div>
    );
  }

  if (isError) {
    return (
      <p className={cn('px-3 text-body-s text-text-secondary', className)}>
        {t('Could not load sports menu.', { defaultValue: 'Could not load sports menu.' })}
      </p>
    );
  }

  return (
    <nav
      aria-label={t('Sports menu', { defaultValue: 'Sports menu' })}
      className={cn('flex flex-col gap-3 overflow-clip', className)}
    >
      <HorizontalScrollRow
        aria-label={t('Sports', { defaultValue: 'Sports' })}
        className="items-center"
      >
        {sortedSports.map((sport) => {
          const slug = toSlug(sport.Name);
          const selected = slug === activeSportSlug;
          const label = translateSportsLabel(sport.Id, sport.Name);
          return (
            <button
              key={sport.Id}
              type="button"
              role="listitem"
              onClick={() => onSportSelect(slug)}
              className={cn(
                'flex min-w-[72px] shrink-0 flex-col items-center justify-center gap-1 rounded-md p-2 leading-none',
                selected && 'bg-bg-elevated',
              )}
            >
              <span className="flex size-6 shrink-0 items-center justify-center">
                <SportIcon
                  name={sportIcon(slug)}
                  size={24}
                  className={selected ? 'text-icon-default' : 'text-icon-muted'}
                />
              </span>
              <span
                className={cn(
                  'text-label-s leading-4 whitespace-nowrap tracking-[0.2px]',
                  selected ? 'text-text-primary' : 'text-text-secondary',
                )}
              >
                {label}
              </span>
            </button>
          );
        })}
      </HorizontalScrollRow>

      {activeSport && (
        <HorizontalScrollRow aria-label={t('Categories', { defaultValue: 'Categories' })}>
          {categories.map((category) => {
            const categorySlug = toSlug(category.Name);
            const selected = categorySlug === selectedCategorySlug;
            const label = translateSportsLabel(category.Id, category.Name);
            const isTopLeagues = category.Name.toLowerCase().includes('top league');
            const flagSrc = getCategoryFlag(category.Name);

            return (
              <button
                key={category.Id}
                type="button"
                role="listitem"
                onClick={() =>
                  onCategorySelect(selected ? null : categorySlug)
                }
                className={cn(
                  'flex shrink-0 items-center justify-center gap-2 rounded-md bg-bg-elevated py-2 pr-3 pl-2',
                  selected ? 'bg-action-primary-hover text-text-on-action' : 'text-text-secondary',
                )}
              >
                {isTopLeagues ? (
                  <span className="flex size-6 shrink-0 items-center justify-center overflow-clip rounded-full bg-icon-default">
                    <Icon
                      name="topleagues"
                      size={16}
                      className={selected ? 'text-action-primary-hover' : 'text-icon-muted'}
                    />
                  </span>
                ) : flagSrc ? (
                  <span className="flex size-6 shrink-0 items-center justify-center overflow-clip rounded-full">
                    <img src={flagSrc} alt="" className="size-full object-cover" />
                  </span>
                ) : (
                  <span className="flex size-6 shrink-0 items-center justify-center overflow-clip rounded-full bg-bg-surface">
                    <span className="text-[8px] font-bold leading-none text-text-secondary">
                      {label.slice(0, 2).toUpperCase()}
                    </span>
                  </span>
                )}
                <span className="text-label-s whitespace-nowrap tracking-[0.2px]">{label}</span>
              </button>
            );
          })}
        </HorizontalScrollRow>
      )}

      {selectedCategory && (
        <HorizontalScrollRow aria-label={t('Tournaments', { defaultValue: 'Tournaments' })}>
          {tournaments.map((tournament) => {
            const tournamentSlug = toSlug(tournament.Name);
            const selected = tournamentSlug === selectedTournamentSlug;
            const label = translateSportsLabel(tournament.Id, tournament.Name);
            const crestSrc = getTournamentOrCategoryBadge(
              tournament.Name,
              selectedCategory.Name,
            );
            const categorySlug = toSlug(selectedCategory.Name);

            return (
              <Link
                key={tournament.Id}
                role="listitem"
                to="/sports/$sport/$category/$tournament"
                params={{
                  sport: activeSportSlug,
                  category: categorySlug,
                  tournament: tournamentSlug,
                }}
                className={cn(
                  'flex shrink-0 items-center justify-center gap-2 rounded-md bg-bg-elevated py-2 pr-3 pl-2',
                  selected ? 'bg-action-primary-hover text-text-on-action' : 'text-text-secondary',
                )}
              >
                <span
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center overflow-clip rounded-full',
                    crestSrc && !isFlagBadgeSrc(crestSrc) && 'bg-white',
                  )}
                >
                  {crestSrc ? (
                    <img
                      src={crestSrc}
                      alt=""
                      className={
                        isFlagBadgeSrc(crestSrc)
                          ? 'size-full object-cover'
                          : 'size-full object-contain p-0.5'
                      }
                    />
                  ) : (
                    <span className="text-[8px] font-bold leading-none text-gray-500">
                      {label.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="text-label-s whitespace-nowrap tracking-[0.2px]">{label}</span>
              </Link>
            );
          })}
        </HorizontalScrollRow>
      )}
    </nav>
  );
}
